import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Vault from "@/pages/Vault";
import Tracker from "@/pages/Tracker";
import Documents from "@/pages/Documents";
import Profile from "@/pages/Profile";
import PersonalExamples from "@/pages/PersonalExamples";
// SavedLetters route removed — now redirects to /vault-coach?section=cover&sub=saved (Phase 2 refactor).
import VaultCoach from "@/pages/VaultCoach";
import Compare from "@/pages/Compare";
import Wellbeing from "@/pages/Wellbeing";
import Community from "@/pages/Community";
import Autofill from "@/pages/Autofill";
import Billing from "@/pages/Billing";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Cookies from "@/pages/Cookies";
import Guide from "@/pages/Guide";
import Contact from "@/pages/Contact";
import Settings from "@/pages/Settings";
import LoadingVault from "@/components/LoadingVault";
import CookieBanner from "@/components/CookieBanner";

const BILLING_ENABLED = process.env.REACT_APP_BILLING_ENABLED === "true";
import PublicShare from "@/pages/PublicShare";
import Unsubscribe from "@/pages/Unsubscribe";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import "@/App.css";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <LoadingVault/>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <LoadingVault/>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

// Backward-compat redirects (preserve query string)
function RedirectWithQuery({ to }) {
  const loc = useLocation();
  return <Navigate to={`${to}${loc.search}`} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <Toaster richColors position="top-right" />
        <BrowserRouter>
          <CookieBanner />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password/:token" element={<PublicOnly><ResetPassword /></PublicOnly>} />
            <Route path="/v/:slug" element={<PublicShare />} />
            <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/vault" element={<Protected><Vault /></Protected>} />
            <Route path="/documents" element={<Protected><Documents /></Protected>} />
            <Route path="/tracker" element={<Protected><Tracker /></Protected>} />
            <Route path="/examples" element={<Protected><PersonalExamples /></Protected>} />
            <Route path="/saved-letters" element={<Protected><Navigate to="/vault-coach?section=cover&sub=saved" replace /></Protected>} />
            <Route path="/vault-coach" element={<Protected><VaultCoach /></Protected>} />
            {/* Backward-compat: /coach and /tailor → /vault-coach */}
            <Route path="/coach" element={<Protected><RedirectWithQuery to="/vault-coach" /></Protected>} />
            <Route path="/tailor" element={<Protected><Navigate to="/vault-coach?section=cv&sub=tailored" replace /></Protected>} />
            <Route path="/compare" element={<Protected><Compare /></Protected>} />
            <Route path="/wellbeing" element={<Protected><Wellbeing /></Protected>} />
            <Route path="/community" element={<Protected><Community /></Protected>} />
            <Route path="/autofill" element={<Protected><Autofill /></Protected>} />
            <Route path="/guide" element={<Protected><Guide /></Protected>} />
            <Route path="/contact" element={<Protected><Contact /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            {BILLING_ENABLED && <Route path="/billing" element={<Protected><Billing /></Protected>} />}
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
